<template>
  <div class="tolerance-input-row-outer">
    <div
      class="tolerance-input-row"
      :class="{'has-errors': errorMsg || isFreeSym || isFreeAsym,
               'input-only': !hasToleranceOptions,
               'read-only-dropdown': readonlyDropdown }"
    >
      <div class="input-section">
        <label
          class="input-label"
          :for="`tolerance-input-${fieldKey || 'default'}`"
        >
          {{ label }} [{{ unitLabel }}]
        </label>
        <NumericInput
          :id="`tolerance-input-${fieldKey || 'default'}`"
          v-model="inputValue"
          :placeholder="placeholder"
          :decimals="decimalPlaces"
          :step="stepValue"
          :min="0"
          :format="formatString"
          :new-style="true"
          class="tolerance-input"
          @focus="emits('focus', $event)"
        />
        <div class="min-max-text">
          {{ minMaxText }}
        </div>
      </div>
      <div
        v-if="hasToleranceOptions"
        class="divider"
      />
      <div
        v-if="hasToleranceOptions"
        class="tolerance-section"
      >
        <label
          class="tolerance-label"
          :for="`tolerance-dropdown-${fieldKey || 'default'}`"
        >
          {{ toleranceLabel }}
        </label>
        <DropDown
          v-if="!readonlyDropdown"
          :id="`tolerance-dropdown-${fieldKey || 'default'}`"
          v-model="toleranceValue"
          :data-items="toleranceOptions"
          :text-field="'text'"
          :value-field="'value'"
          :data-item-key="'value'"
          class="tolerance-dropdown"
          @change="handleToleranceChange"
        />
        <div
          v-else
          class="tolerance-readonly-text"
        >
          {{ displayToleranceText }}
        </div>
        <div
          class="tolerance-text"
          v-if="displayToleranceTextValue"
        >
          {{ displayToleranceTextValue }}
        </div>
      </div>
    </div>
    <div
      v-if="isFreeSym || isFreeAsym"
      class="free-tolerance-container"
      :class="{'has-error': errorMsg}"
    >
      <div
        v-if="isFreeSym"
        class="free-tolerance-single"
      >
        <label
          class="input-label"
          :for="`free-tolerance-${fieldKey || 'default'}`"
        >
          {{ tv('Frontend.Text.ToleranceInput', 'ORingConfiguratorImport') }} [{{ unitLabel }}]
        </label>
        <NumericInput
          :id="`free-tolerance-${fieldKey || 'default'}`"
          v-model="freeToleranceValue"
          :placeholder="'0.000'"
          :decimals="decimalPlaces"
          :step="stepValue"
          :format="formatString"
          :new-style="true"
          class="free-tolerance-numeric-input"
          @input="handleFreeToleranceInput"
          @focusin="(e: any) => e.target.select()"
        />
      </div>
      <div
        v-if="isFreeAsym"
        class="free-tolerance-dual"
      >
        <div class="free-tolerance-input">
          <label
            class="input-label"
            :for="`free-tolerance-min-${fieldKey || 'default'}`"
          >
            {{ tv('Frontend.Tolerance.Min', 'ORingConfiguratorImport') }} [{{ unitLabel }}]
          </label>
          <NumericInput
            :id="`free-tolerance-min-${fieldKey || 'default'}`"
            v-model="freeToleranceMin"
            :placeholder="'0.000'"
            :decimals="decimalPlaces"
            :step="stepValue"
            :format="formatString"
            :new-style="true"
            class="free-tolerance-numeric-input"
            @input="handleFreeToleranceInput"
            @focusin="(e: any) => e.target.select()"
          />
        </div>
        <div class="free-dual-divider" />
        <div class="free-tolerance-input">
          <label
            class="input-label"
            :for="`free-tolerance-max-${fieldKey || 'default'}`"
          >
            {{ tv('Frontend.Tolerance.Max', 'ORingConfiguratorImport') }} [{{ unitLabel }}]
          </label>
          <NumericInput
            :id="`free-tolerance-max-${fieldKey || 'default'}`"
            v-model="freeToleranceMax"
            :placeholder="'0.000'"
            :decimals="decimalPlaces"
            :step="stepValue"
            :format="formatString"
            :new-style="true"
            class="free-tolerance-numeric-input"
            @input="handleFreeToleranceInput"
            @focusin="(e: any) => e.target.select()"
          />
        </div>
      </div>
    </div>
    <div
      v-if="errorMsg"
      class="error-section"
    >
      <Markdown
        class="error-text"
        :text="errorMsg"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import NumericInput from '~/components/input-components/NumericInput.vue'
import DropDown from '~/components/input-components/DropDown.vue'
import Markdown from '~/components/Markdown.vue'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import useI18nExtended from '~/composables/usei18nExtended'
import {useToleranceFormatter} from '~/composables/oring/useToleranceFormatter'

const oringStore = useORingCalculatorStore()
const {oringCalculatorImperial, oringDesignCockpitCurrentHousing} = storeToRefs(oringStore)
const {tv} = useI18nExtended()
const {formatValue} = useToleranceFormatter()

const freeToleranceValue = ref<number | null>(null)
const freeToleranceMax = ref<number | null>(null)
const freeToleranceMin = ref<number | null>(null)

interface ToleranceOption {
  text: string
  value: string | null
}

interface Props {
  modelValue: {
    input: number | null
    tolerance: ToleranceOption | null
    freeToleranceValue?: number | null
    freeToleranceMax?: number | null
    freeToleranceMin?: number | null
  }
  label: string
  placeholder?: string
  toleranceLabel?: string
  toleranceOptions?: ToleranceOption[]
  minMaxText?: string
  toleranceText?: string
  errorMsg?: string
  readonlyDropdown?: boolean
  fieldKey?: string // Add field key to access housing data
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  toleranceLabel: 'Tolerance',
  toleranceOptions: () => [],
  minMaxText: '',
  toleranceText: '',
  errorMsg: '',
  readonlyDropdown: false,
  fieldKey: ''
})

const emits = defineEmits<{
  'update:modelValue': [value: {input: number | null, tolerance: ToleranceOption | null, freeToleranceValue?: number | null, freeToleranceMax?: number | null, freeToleranceMin?: number | null}],
  'focus': [event: FocusEvent]
}>()

const inputValue = computed({
  get: () => props.modelValue?.input ?? null,
  set: (value) => {
    emits('update:modelValue', {
      input: value,
      tolerance: props.modelValue?.tolerance ?? null,
      freeToleranceValue: freeToleranceValue.value,
      freeToleranceMax: freeToleranceMax.value,
      freeToleranceMin: freeToleranceMin.value
    })
  }
})

const toleranceValue = computed({
  get: () => {
    return props.modelValue?.tolerance ?? null
  },
  set: (value) => {
    if (!props.readonlyDropdown) {
      emits('update:modelValue', {
        input: props.modelValue?.input ?? null,
        tolerance: value, // value is the full object (ToleranceOption | null)
        freeToleranceValue: freeToleranceValue.value,
        freeToleranceMax: freeToleranceMax.value,
        freeToleranceMin: freeToleranceMin.value
      })
    }
  }
})

const displayToleranceText = computed(() => {
  if (!props.readonlyDropdown) {
    const tolerance = toleranceValue.value
    return tolerance?.text || ''
  }

  // In readonly mode, show actual tolerance values from housing data
  if (props.fieldKey && oringStore.oringAssistantCurrentHousing) {
    const housing = oringStore.oringAssistantCurrentHousing
    let toleranceMax: number | null = null
    let toleranceMin: number | null = null

    // Map field key for some fields to housing tolerance data
    // ignore d1-d10 whatever because we dont need those
    switch (props.fieldKey) {
    case 'b1in':
    case 'b1out':
      toleranceMax = housing.b1?.toleranceMax ?? null
      toleranceMin = housing.b1?.toleranceMin ?? null
      break
    case 'b4':
      toleranceMax = housing.b4?.toleranceMax ?? null
      toleranceMin = housing.b4?.toleranceMin ?? null
      break
    case 'height':
      toleranceMax = housing.h?.toleranceMax ?? null
      toleranceMin = housing.h?.toleranceMin ?? null
      break
    }

    // Format tolerance value like in ORingDesignSpaceAssistantStep5
    if (toleranceMax !== null && typeof toleranceMax === 'number') {
      const unit = unitLabel.value
      if (toleranceMin !== null && typeof toleranceMin === 'number' && toleranceMin !== toleranceMax) {
        // Show min and max if they differ
        const decimals = oringCalculatorImperial.value ? 3 : 2
        const minFormatted = toleranceMin.toFixed(decimals)
        const maxFormatted = toleranceMax.toFixed(decimals)
        return `${minFormatted} ${unit} / ${maxFormatted} ${unit}`
      } else {
        return `+ ${toleranceMax.toFixed(3)} ${unit}`
      }
    }
  }

  // Fallback to tolerance class name if we can't extract the values
  const tolerance = toleranceValue.value
  return tolerance?.text || ''
})

// Create an immediate ref to track initialization state
const isInitializing = ref(true)

// After initial mount, set initializing to false
onMounted(() => {
  // Use nextTick to ensure all props are properly initialized first
  nextTick(() => {
    setTimeout(() => {
      isInitializing.value = false
    }, 100) // Longer delay to ensure everything is properly loaded
  })
  // Initialize free tolerance numeric refs from incoming model value (if present)
  freeToleranceValue.value = props.modelValue?.freeToleranceValue ?? null
  freeToleranceMin.value = props.modelValue?.freeToleranceMin ?? null
  freeToleranceMax.value = props.modelValue?.freeToleranceMax ?? null
})

const hasToleranceOptions = computed(() => {
  // Always show tolerance options during initialization phase
  if (isInitializing.value) {
    return true
  }

  // After initialization, check the actual options
  return props.toleranceOptions && props.toleranceOptions.length > 0
})

const unitLabel = computed(() => {
  return oringCalculatorImperial.value ? tv('inch', 'ORingConfigurator') : tv('mm', 'ORingConfigurator')
})

const decimalPlaces = computed(() => {
  return oringCalculatorImperial.value ? 4 : 3
})

const stepValue = computed(() => {
  return oringCalculatorImperial.value ? 0.01 : 0.1
})

const formatString = computed(() => {
  return oringCalculatorImperial.value ? 'n4' : 'n3'
})

const errorMsg = computed(() => {
  // Hide error if input is empty
  return props.modelValue && props.modelValue.input !== null && props.modelValue.input !== undefined
    ? props.errorMsg
    : ''
})

const isFreeSym = computed(() => {
  const toleranceText = props.modelValue?.tolerance?.text?.toLowerCase() || ''
  return toleranceText.includes('free') && toleranceText.includes('sym') && !toleranceText.includes('asym')
})

const isFreeAsym = computed(() => {
  const toleranceText = props.modelValue?.tolerance?.text?.toLowerCase() || ''
  return toleranceText.includes('free') && toleranceText.includes('asym')
})

const getHousingFieldKey = (fieldKey: string): string => {
  if (fieldKey === 'b1in' || fieldKey === 'b1out') {
    return 'b1'
  }
  if (fieldKey === 'height') {
    return 'h'
  }
  return fieldKey
}

const displayToleranceTextValue = computed(() => {
  const unit = oringCalculatorImperial.value ? tv('inch', 'ORingConfigurator') : tv('mm', 'ORingConfigurator')

  // For free sym, use the ± format with the symmetric value
  if (isFreeSym.value && freeToleranceValue.value !== null) {
    const formatted = formatValue(freeToleranceValue.value)
    return `${tv('Frontend.Text.Tolerance', 'ORingConfiguratorImport')} ± ${formatted} ${unit}`
  }

  // For free asym, use the free tolerance values
  if (isFreeAsym.value && freeToleranceMin.value !== null && freeToleranceMax.value !== null) {
    const minFormatted = formatValue(freeToleranceMin.value)
    const maxFormatted = formatValue(freeToleranceMax.value)
    return `${tv('Frontend.Text.Tolerance', 'ORingConfiguratorImport')} ${minFormatted} ${unit} / ${maxFormatted} ${unit}`
  }

  // For all other tolerance types, check housing data
  if (props.fieldKey && oringDesignCockpitCurrentHousing.value) {
    const housing = oringDesignCockpitCurrentHousing.value
    const housingFieldKey = getHousingFieldKey(props.fieldKey)
    const field = housing[housingFieldKey as keyof typeof housing] as any
    const toleranceMin = field?.toleranceMin ?? null
    const toleranceMax = field?.toleranceMax ?? null

    if (toleranceMin !== null && toleranceMax !== null && toleranceMin !== toleranceMax) {
      const minFormatted = formatValue(toleranceMin)
      const maxFormatted = formatValue(toleranceMax)
      return `${tv('Frontend.Text.Tolerance', 'ORingConfiguratorImport')} ${minFormatted} ${unit} / ${maxFormatted} ${unit}`
    }
  }

  return props.toleranceText
})

let freeToleranceInputTimer: ReturnType<typeof setTimeout> | null = null
const handleFreeToleranceInput = () => {
  if (isFreeAsym.value && freeToleranceMax.value !== null && freeToleranceMin.value !== null) {
    if (freeToleranceMax.value < freeToleranceMin.value) {
      freeToleranceMax.value = freeToleranceMin.value
    }
  }

  if (freeToleranceInputTimer) {
    clearTimeout(freeToleranceInputTimer)
  }
  freeToleranceInputTimer = setTimeout(() => {
    emitFreeToleranceValues()
  }, 500)
}

const handleToleranceChange = () => {
  nextTick(() => {
    if (isFreeSym.value) {
      // If we already have asym values, use the max value for symmetric
      if (freeToleranceMax.value !== null || freeToleranceMin.value !== null) {
        freeToleranceValue.value = freeToleranceMax.value ?? freeToleranceMin.value ?? null
      } else if (freeToleranceValue.value === null) {
        // No existing value, use current housing tolerance as default
        const housing = oringDesignCockpitCurrentHousing.value
        if (housing && props.fieldKey) {
          const housingFieldKey = getHousingFieldKey(props.fieldKey)
          const field = housing[housingFieldKey as keyof typeof housing] as any
          freeToleranceValue.value = (field?.toleranceMax !== null && field?.toleranceMax !== undefined) ? field.toleranceMax : null
        }
      }

      // Clear asymmetric fields when in symmetric mode
      freeToleranceMax.value = null
      freeToleranceMin.value = null
      emitFreeToleranceValues()
    } else if (isFreeAsym.value) {
      // If a symmetric value exists, use it to initialize asym range.
      if (freeToleranceValue.value !== null) {
        const v = Math.abs(freeToleranceValue.value)
        freeToleranceMax.value = v
        freeToleranceMin.value = -v
      } else if (freeToleranceMax.value === null && freeToleranceMin.value === null) {
        // No existing values, use current housing tolerances as defaults
        const housing = oringDesignCockpitCurrentHousing.value
        if (housing && props.fieldKey) {
          const housingFieldKey = getHousingFieldKey(props.fieldKey)
          const field = housing[housingFieldKey as keyof typeof housing] as any
          freeToleranceMax.value = (field?.toleranceMax !== null && field?.toleranceMax !== undefined) ? field.toleranceMax : null
          freeToleranceMin.value = (field?.toleranceMin !== null && field?.toleranceMin !== undefined) ? field.toleranceMin : null
        }
      }

      // Clear symmetric field when in asymmetric mode
      freeToleranceValue.value = null
      emitFreeToleranceValues()
    } else {
      // Not free mode: clear everything
      freeToleranceValue.value = null
      freeToleranceMax.value = null
      freeToleranceMin.value = null
    }
  })
}

const emitFreeToleranceValues = () => {
  emits('update:modelValue', {
    input: props.modelValue?.input ?? null,
    tolerance: props.modelValue?.tolerance ?? null,
    freeToleranceValue: freeToleranceValue.value,
    freeToleranceMax: freeToleranceMax.value,
    freeToleranceMin: freeToleranceMin.value
  })
}
</script>

<style lang="scss" scoped>
.tolerance-input-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  &.has-errors {
    margin-bottom: .5rem;
  }

  &.input-only {
    .input-section {
      max-width: 13rem; // 208px converted to rem, limit width when no tolerance dropdown
    }
  }

  &.read-only-dropdown {
    width: fit-content; // Only take up the space needed for content
    max-width: fit-content;

    .divider {
      position: relative;
      top: .5rem;
    }

    &.tolerance-input-row {
      margin-bottom: .5rem;
    }

    // Override for readonly dropdown mode - sections should only take needed space
    .input-section,
    .tolerance-section {
      flex: 0 0 auto; // Don't flex, use content size
      max-width: none; // Remove max-width constraint
    }
  }
}

.input-section,
.tolerance-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  position: relative;
  min-width: 0; // Allow flex items to shrink below their content size
  max-width: 50%; // Ensure each section doesn't exceed half the container
  overflow: visible;
}

.input-label {
  color: $color__text__default;
  font-size: 0.9rem;
  font-weight: $base-text-font-weight-light;
  white-space: nowrap;
  overflow: visible;
  width: 0; // Don't take up any space in the flex layout
}

.tolerance-input {
  width: 100%;
  min-width: 11.6875rem; // 187px
  max-width: 11.6875rem; // Force exact width of 187px
}

.tolerance-dropdown {
  width: 100%;
  min-width: 11.6875rem; // 187px
  max-width: 11.6875rem; // Force exact width of 187px
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  :deep(.k-input-inner) {
    font-weight: $base-text-font-weight-semi-bold;
  }
}

.tolerance-readonly-text {
  line-height: 2.5rem;
  color: $color--text__default;
  font-weight: $base-text-font-weight-semi-bold;
}

.min-max-text,
.tolerance-text {
  font-size: 0.75rem;
  font-weight: $base-text-font-weight-light;
  margin-top: 0;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.divider {
  width: 1.25rem;
  height: 0.125rem;
  background-color: $color__fst__gray__020;
  flex-shrink: 0;
}

.tolerance-label {
  color: $color__text__default;
  font-size: 0.9rem;
  font-weight: $base-text-font-weight-light;
  white-space: nowrap;
  overflow: visible;
  width: 0; // Don't take up any space in the flex layout
  min-width: max-content; // Allow the label to show its full content
}

.error-section {
  width: 100%;
  margin: 0 0 1rem;
}

.error-text {
  color: $color__text--error;
  font-size: 0.75rem;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;

  :deep(strong) {
    font-weight: $base-text-font-weight-semi-bold;
  }
}

.free-tolerance-container {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  width: 100%;
  margin-bottom: 2rem;

  &.has-error {
    margin-bottom: 0;
  }
}

.free-tolerance-single {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 9.6875rem; // 155px
  margin-left: 13rem;
}

.free-tolerance-dual {
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
}

.free-tolerance-input {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.free-tolerance-numeric-input {
  width: 100%;
  min-width: 9.6875rem;
  max-width: 9.6875rem;
}

.free-dual-divider {
  width: 1.25rem;
  height: 0.125rem;
  margin-top: 1rem;
  background-color: $color__fst__gray__020;
  flex-shrink: 0;
}

@include breakpoint-down($md) {
  .tolerance-input-row {
    flex-direction: column;
    gap: 0.5rem; // reduce space between input and tolerance on mobile
    align-items: stretch;
    width: 100%;
    max-width: 100%;
    margin-bottom: 0.5rem; // smaller block height on mobile
  }

  .input-section,
  .tolerance-section {
    max-width: 100%;
    width: 100%; // ensure full width on mobile
  }

  .divider {
    display: none; // hide the small divider between input and tolerance on mobile
  }

  // Make the inputs and tolerance dropdown span full width on mobile
  .tolerance-input,
  .tolerance-dropdown {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }

  .min-max-text,
  .tolerance-text {
    margin-top: 0;
    max-width: 100%;
  }

  .error-section {
    width: 100%;
  }

  // In mobile readonly mode, put label and value on the same line
  .tolerance-input-row.read-only-dropdown {
    width: 100%;
    max-width: 100%; // override desktop fit-content on mobile

    .tolerance-section {
      display: flex;
      flex-flow: row wrap; // allow helper text to go below
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      width: 100%;
    }

    .input-section,
    .tolerance-section {
      flex: 1 1 auto;
      max-width: 100%;
      width: 100%;
    }

    .tolerance-label {
      width: auto; // allow natural width
      margin-right: 0.25rem;
    }

    .tolerance-readonly-text {
      white-space: nowrap; // keep value on one line
      line-height: 1.25rem; // even tighter line-height to reduce row height
    }

    .tolerance-text {
      flex: 0 0 100%; // move helper text to next line
      margin-top: 0.25rem;
    }
  }

  // Free tolerance inputs on mobile
  .free-tolerance-container {
    justify-content: stretch;
  }

  .free-tolerance-single,
  .free-tolerance-dual {
    width: 100%;
  }

  .free-tolerance-dual {
    flex-direction: column;
    gap: 0.5rem;
    justify-content: stretch;
    align-items: stretch;
  }

  .free-dual-divider {
    display: none;
  }

  .free-tolerance-numeric-input {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }

  .free-tolerance-single {
    margin-left: 0;
  }
}
</style>
